import { Fragment, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, SearchIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SelectInput, Button } from '../../components';
import { fetchStores } from '../../services/storeService';
import { calculateVolumetricWeight } from '../../services/shippingPackageServices';
import { debounce } from 'lodash';
import { PACKAGE_TYPES } from './constants';

export const ShippingPackageFormModal = ({
    open,
    setOpen,
    packageData = null,
    onSubmit,
    isLoading = false
}) => {
    const { i18n } = useTranslation();
    const { warehouse_id } = useParams();
    const cancelButtonRef = useRef(null);
    const isEditing = !!packageData;

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        length_cm: '',
        width_cm: '',
        height_cm: '',
        weight_kg: '',
        package_type: null,
        bar_code: '',
        volume_liters: '',
        cost: '0',
        store_id: null,
        store: null
    });

    const [errors, setErrors] = useState({});
    
    // Store search state
    const [storeSearchQuery, setStoreSearchQuery] = useState('');
    const [storeSearchResults, setStoreSearchResults] = useState([]);
    const [isSearchingStores, setIsSearchingStores] = useState(false);
    const [showStoreDropdown, setShowStoreDropdown] = useState(false);
    
    // Volumetric weight calculation
    const [calculatedVolumetricWeight, setCalculatedVolumetricWeight] = useState(null);
    const [isCalculatingWeight, setIsCalculatingWeight] = useState(false);
    const [originalVolumetricWeight, setOriginalVolumetricWeight] = useState(null);
    const weightCalcSessionRef = useRef(0);
    const skipDimensionEffectRef = useRef(false);

    // Track original package type for customer_box -> other change
    const [originalPackageType, setOriginalPackageType] = useState(null);

    // Get translated package type options
    const packageTypeOptions = PACKAGE_TYPES.map(option => ({
        id: option.id,
        name: i18n.t(`shipping_packages.package_types.${option.id}`)
    }));

    // Debounced store search
    const debouncedStoreSearch = useCallback(
        debounce(async (query) => {
            if (!query || query.length < 2) {
                setStoreSearchResults([]);
                return;
            }
            
            setIsSearchingStores(true);
            try {
                const response = await fetchStores({ 
                    name: query, 
                    warehouse_id: warehouse_id,
                    per_page: 10
                });
                setStoreSearchResults(response.stores || []);
            } catch {
                setStoreSearchResults([]);
            }
            setIsSearchingStores(false);
        }, 300),
        [warehouse_id]
    );

    // Debounced volumetric weight calculation
    const debouncedCalculateWeight = useCallback(
        debounce(async (length, width, height, sessionId) => {
            if (!length || !width || !height ||
                parseInt(length) <= 0 || parseInt(width) <= 0 || parseInt(height) <= 0) {
                if (weightCalcSessionRef.current === sessionId) {
                    setCalculatedVolumetricWeight(null);
                    setIsCalculatingWeight(false);
                }
                return;
            }

            setIsCalculatingWeight(true);
            try {
                const response = await calculateVolumetricWeight({
                    length_cm: parseInt(length),
                    width_cm: parseInt(width),
                    height_cm: parseInt(height),
                    warehouse_id: warehouse_id
                });
                if (weightCalcSessionRef.current === sessionId) {
                    setCalculatedVolumetricWeight(response.volumetric_weight);
                }
            } catch {
                if (weightCalcSessionRef.current === sessionId) {
                    setCalculatedVolumetricWeight(null);
                }
            }
            if (weightCalcSessionRef.current === sessionId) {
                setIsCalculatingWeight(false);
            }
        }, 500),
        [warehouse_id]
    );

    // Reset form when modal opens/closes or when packageData changes
    useEffect(() => {
        if (open && packageData) {
            // Editing mode - invalidate previous session and start new one
            weightCalcSessionRef.current += 1;
            debouncedCalculateWeight.cancel();
            const selectedPackageType = packageTypeOptions.find(
                opt => opt.id === packageData.package_type
            );
            setFormData({
                name: packageData.name || '',
                length_cm: packageData.length_cm?.toString() || '',
                width_cm: packageData.width_cm?.toString() || '',
                height_cm: packageData.height_cm?.toString() || '',
                weight_kg: packageData.weight_kg?.toString() || '',
                package_type: selectedPackageType || null,
                bar_code: packageData.bar_code || '',
                volume_liters: packageData.volume_liters?.toString() || '',
                cost: packageData.cost?.toString() || '',
                store_id: packageData.store_id || null,
                store: packageData.store || null
            });
            setOriginalPackageType(packageData.package_type);
            setOriginalVolumetricWeight(packageData.volumetric_weight || null);
            setCalculatedVolumetricWeight(null);
            setIsCalculatingWeight(true);

            // If editing and has store, set the search query to store name
            if (packageData.store?.name) {
                setStoreSearchQuery(packageData.store.name);
            }
        } else if (open && !packageData) {
            // Creating mode - invalidate previous session
            weightCalcSessionRef.current += 1;
            debouncedCalculateWeight.cancel();
            skipDimensionEffectRef.current = true;
            setFormData({
                name: '',
                length_cm: '',
                width_cm: '',
                height_cm: '',
                weight_kg: '',
                package_type: null,
                bar_code: '',
                volume_liters: '',
                cost: '0',
                store_id: null,
                store: null
            });
            setOriginalPackageType(null);
            setOriginalVolumetricWeight(null);
            setCalculatedVolumetricWeight(null);
            setIsCalculatingWeight(false);
            setStoreSearchQuery('');
        } else if (!open) {
            // Modal closed - invalidate any pending calculations
            weightCalcSessionRef.current += 1;
            debouncedCalculateWeight.cancel();
            setCalculatedVolumetricWeight(null);
            setIsCalculatingWeight(false);
        }
        setErrors({});
        setStoreSearchResults([]);
        setShowStoreDropdown(false);
    }, [open, packageData]);

    // Calculate volumetric weight when dimensions change
    useEffect(() => {
        if (skipDimensionEffectRef.current) {
            skipDimensionEffectRef.current = false;
            debouncedCalculateWeight.cancel();
            return;
        }
        if (open && (formData.length_cm || formData.width_cm || formData.height_cm)) {
            debouncedCalculateWeight(formData.length_cm, formData.width_cm, formData.height_cm, weightCalcSessionRef.current);
        }
    }, [formData.length_cm, formData.width_cm, formData.height_cm, open, debouncedCalculateWeight]);

    // Search stores when query changes
    useEffect(() => {
        if (storeSearchQuery && showStoreDropdown) {
            debouncedStoreSearch(storeSearchQuery);
        }
    }, [storeSearchQuery, showStoreDropdown, debouncedStoreSearch]);

    const isCustomerBox = useMemo(() => formData.package_type?.id === 'customer_box', [formData.package_type]);
    const wasCustomerBox = originalPackageType === 'customer_box';

    const isFormValid = useMemo(() => {
        if (!formData.name.trim()) return false;
        if (!formData.package_type) return false;
        if (!formData.length_cm || parseInt(formData.length_cm) <= 0) return false;
        if (!formData.width_cm || parseInt(formData.width_cm) <= 0) return false;
        if (!formData.height_cm || parseInt(formData.height_cm) <= 0) return false;
        if (!formData.weight_kg || parseFloat(formData.weight_kg) < 0) return false;
        if (isCustomerBox && !formData.store_id) return false;
        if (formData.cost === '' || formData.cost === null || formData.cost === undefined) return false;
        if (parseFloat(formData.cost) < 0) return false;
        return true;
    }, [formData.name, formData.package_type, formData.length_cm, formData.width_cm, formData.height_cm, formData.weight_kg, formData.store_id, formData.cost, isCustomerBox]);

    const hasChanges = useMemo(() => {
        if (!isEditing || !packageData) return true;
        const str = (val) => String(val ?? '');
        if (formData.name.trim() !== (packageData.name || '')) return true;
        if (str(formData.length_cm) !== str(packageData.length_cm)) return true;
        if (str(formData.width_cm) !== str(packageData.width_cm)) return true;
        if (str(formData.height_cm) !== str(packageData.height_cm)) return true;
        if (str(formData.weight_kg) !== str(packageData.weight_kg)) return true;
        if ((formData.package_type?.id || null) !== (packageData.package_type || null)) return true;
        if ((formData.bar_code || '') !== (packageData.bar_code || '')) return true;
        if (str(formData.volume_liters) !== str(packageData.volume_liters)) return true;
        if (str(formData.cost) !== str(packageData.cost)) return true;
        if ((formData.store_id || null) !== (packageData.store_id || null)) return true;
        if (calculatedVolumetricWeight && originalVolumetricWeight &&
            String(calculatedVolumetricWeight) !== String(originalVolumetricWeight)) return true;
        return false;
    }, [formData, packageData, isEditing, calculatedVolumetricWeight, originalVolumetricWeight]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = true;
        }
        if (!formData.package_type) {
            newErrors.package_type = true;
        }
        if (!formData.length_cm || parseInt(formData.length_cm) <= 0) {
            newErrors.length_cm = true;
        }
        if (!formData.width_cm || parseInt(formData.width_cm) <= 0) {
            newErrors.width_cm = true;
        }
        if (!formData.height_cm || parseInt(formData.height_cm) <= 0) {
            newErrors.height_cm = true;
        }
        if (!formData.weight_kg || parseFloat(formData.weight_kg) < 0) {
            newErrors.weight_kg = true;
        }
        // Require store for customer_box
        if (isCustomerBox && !formData.store_id) {
            newErrors.store = true;
        }
        // Cost is always required and must be >= 0
        if (formData.cost === '' || formData.cost === null || formData.cost === undefined || parseFloat(formData.cost) < 0) {
            newErrors.cost = true;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const submitData = {
            name: formData.name.trim(),
            length_cm: parseInt(formData.length_cm),
            width_cm: parseInt(formData.width_cm),
            height_cm: parseInt(formData.height_cm),
            weight_kg: parseFloat(formData.weight_kg),
            package_type: formData.package_type?.id || undefined,
            bar_code: formData.bar_code.trim() || undefined,
            volume_liters: formData.volume_liters ? parseFloat(formData.volume_liters) : undefined,
            cost: formData.cost ? parseFloat(formData.cost) : undefined
        };

        // Handle store_id logic
        if (isCustomerBox && formData.store_id) {
            submitData.store_id = formData.store_id;
        } else if (wasCustomerBox && !isCustomerBox) {
            // Changed from customer_box to another type, send null
            submitData.store_id = null;
        }

        // Remove undefined values (but keep null for store_id if needed)
        Object.keys(submitData).forEach(key => {
            if (submitData[key] === undefined) {
                delete submitData[key];
            }
        });

        onSubmit(submitData);
    };

    const handleClose = () => {
        if (!isLoading) {
            setOpen(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
    };

    const handlePackageTypeChange = (value) => {
        setFormData(prev => ({
            ...prev,
            package_type: value,
            // Clear store if changing away from customer_box
            ...(value?.id !== 'customer_box' && { store_id: null, store: null })
        }));
        
        // Clear store search when changing away from customer_box
        if (value?.id !== 'customer_box') {
            setStoreSearchQuery('');
            setStoreSearchResults([]);
        }
    };

    const handleStoreSelect = (store) => {
        setFormData(prev => ({
            ...prev,
            store_id: store.id,
            store: store
        }));
        setStoreSearchQuery(store.name);
        setShowStoreDropdown(false);
        setStoreSearchResults([]);
        if (errors.store) {
            setErrors(prev => ({ ...prev, store: false }));
        }
    };

    const handleStoreSearchChange = (e) => {
        const value = e.target.value;
        setStoreSearchQuery(value);
        setShowStoreDropdown(true);
        
        // Clear selection if user clears the input
        if (!value) {
            setFormData(prev => ({
                ...prev,
                store_id: null,
                store: null
            }));
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                initialFocus={cancelButtonRef}
                onClose={handleClose}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                    {isEditing 
                                        ? i18n.t('shipping_packages.edit_package')
                                        : i18n.t('shipping_packages.add_package')
                                    }
                                </Dialog.Title>
                                <button
                                    type="button"
                                    className="text-gray-400 hover:text-gray-500"
                                    onClick={handleClose}
                                >
                                    <XIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {i18n.t('shipping_packages.form.name')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder={i18n.t('shipping_packages.form.name_placeholder')}
                                            className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                errors.name 
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                            }`}
                                        />
                                    </div>

                                    {/* Dimensions */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.length')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.length_cm}
                                                onChange={(e) => handleInputChange('length_cm', e.target.value)}
                                                className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                    errors.length_cm 
                                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                }`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.width')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.width_cm}
                                                onChange={(e) => handleInputChange('width_cm', e.target.value)}
                                                className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                    errors.width_cm 
                                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                }`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.height')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.height_cm}
                                                onChange={(e) => handleInputChange('height_cm', e.target.value)}
                                                className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                    errors.height_cm 
                                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    {/* Weight (kg) */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {i18n.t('shipping_packages.form.weight_kg')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.weight_kg}
                                            onChange={(e) => handleInputChange('weight_kg', e.target.value)}
                                            className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                errors.weight_kg
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                            }`}
                                        />
                                    </div>

                                    {/* Volumetric Weight Display */}
                                    {(calculatedVolumetricWeight || isCalculatingWeight) && (
                                        <div className="bg-gray-50 rounded-md p-3">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.volumetric_weight')}
                                            </label>
                                            {isCalculatingWeight ? (
                                                <span className="text-sm text-gray-500">{i18n.t('shipping_packages.form.calculating')}</span>
                                            ) : isEditing && originalVolumetricWeight && String(calculatedVolumetricWeight) !== String(originalVolumetricWeight) ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-500 line-through">{originalVolumetricWeight} kg</span>
                                                        <span className="text-sm text-gray-400">→</span>
                                                        <span className="text-lg font-semibold text-indigo-600">{calculatedVolumetricWeight} kg</span>
                                                    </div>
                                                    <p className="text-xs text-amber-600">{i18n.t('shipping_packages.form.weight_will_be_overwritten')}</p>
                                                </div>
                                            ) : (
                                                <span className="text-lg font-semibold text-indigo-600">
                                                    {calculatedVolumetricWeight} kg
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Package Type */}
                                    <SelectInput
                                        label={i18n.t('shipping_packages.form.package_type')}
                                        options={packageTypeOptions}
                                        value={formData.package_type}
                                        onChange={handlePackageTypeChange}
                                        className="w-full"
                                        required
                                    />

                                    {/* Store Search - Only shown for customer_box */}
                                    {isCustomerBox && (
                                        <div className="relative">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.store')} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={storeSearchQuery}
                                                    onChange={handleStoreSearchChange}
                                                    onFocus={() => setShowStoreDropdown(true)}
                                                    onBlur={() => setTimeout(() => setShowStoreDropdown(false), 200)}
                                                    placeholder={i18n.t('shipping_packages.form.store_placeholder')}
                                                    className={`shadow-sm block w-full sm:text-sm rounded-md pr-10 ${
                                                        errors.store 
                                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                                            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                    }`}
                                                />
                                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                    {isSearchingStores ? (
                                                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <SearchIcon className="h-5 w-5 text-gray-400" />
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Store dropdown */}
                                            {showStoreDropdown && storeSearchResults.length > 0 && (
                                                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                    {storeSearchResults.map((store) => (
                                                        <div
                                                            key={store.id}
                                                            onClick={() => handleStoreSelect(store)}
                                                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white"
                                                        >
                                                            <span className="block truncate">{store.name}</span>
                                                            {formData.store_id === store.id && (
                                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                                                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Selected store indicator */}
                                            {formData.store && (
                                                <p className="mt-1 text-sm text-green-600">
                                                    ✓ {i18n.t('shipping_packages.form.store_selected')}: {formData.store.name}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Bar Code */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {i18n.t('shipping_packages.form.bar_code')}
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.bar_code}
                                            onChange={(e) => handleInputChange('bar_code', e.target.value)}
                                            placeholder={i18n.t('shipping_packages.form.bar_code_placeholder')}
                                            className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>

                                    {/* Volume and Cost */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.volume_liters')}
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={formData.volume_liters}
                                                onChange={(e) => handleInputChange('volume_liters', e.target.value)}
                                                className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {i18n.t('shipping_packages.form.cost')}
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={formData.cost}
                                                onChange={(e) => handleInputChange('cost', e.target.value)}
                                                className={`shadow-sm block w-full sm:text-sm rounded-md ${
                                                    errors.cost
                                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                                                }`}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="mt-6 flex justify-end space-x-3">
                                    <Button
                                        type="secondary"
                                        onClick={handleClose}
                                        disabled={isLoading}
                                        className="px-4"
                                    >
                                        {i18n.t('shipping_packages.form.cancel')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        buttonType="submit"
                                        loading={isLoading}
                                        disabled={isCalculatingWeight || !isFormValid || !hasChanges}
                                        className="px-4"
                                    >
                                        {isEditing 
                                            ? i18n.t('shipping_packages.form.update')
                                            : i18n.t('shipping_packages.form.create')
                                        }
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
